import { alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';

export type Tone = 'good' | 'warn' | 'bad' | 'neut';

export function sys(theme: Theme) {
  return theme.palette.custom.OV.sys;
}

export function signature(theme: Theme): string {
  return theme.palette.custom.OV.extended.Signature.color;
}

export function toneColor(theme: Theme, tone: Tone): string {
  if (tone === 'good') {
    return sys(theme).success;
  }
  if (tone === 'bad') {
    return sys(theme).error;
  }
  if (tone === 'warn') {
    return signature(theme);
  }
  return sys(theme).onSurfaceVariant;
}

export const healthSentimentGridSx = {
  display: 'grid',
  gap: 2,
  alignItems: 'stretch',
  gridTemplateColumns: '1fr',
  '@container (min-width: 560px)': {
    gridTemplateColumns: '1fr 1fr',
  },
} as const;

export const pageStackSx = {
  maxWidth: 940,
  minWidth: 0,
  containerType: 'inline-size',
} as const;

export const panelSx = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: sys(theme).surfaceContainerLow,
  border: `1px solid ${alpha(sys(theme).outlineVariant, 0.55)}`,
  borderRadius: '14px',
  padding: '16px 18px',
});

export const panelHeadingSx = (theme: Theme, tight?: boolean) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: alpha(sys(theme).onSurfaceVariant, 0.78),
  margin: tight ? '0 0 4px' : '0 0 13px',
});

export const badgeSx = (theme: Theme, tone: Tone) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: 11.5,
  fontWeight: 700,
  padding: '3px 10px',
  borderRadius: '999px',
  textTransform: 'capitalize',
  color: toneColor(theme, tone),
  backgroundColor: alpha(toneColor(theme, tone), tone === 'good' ? 0.14 : 0.16),
  '& svg': { width: 13, height: 13 },
});

export const donutRootSx = {
  position: 'relative',
  flex: 'none',
  width: 72,
  height: 72,
} as const;

export const donutSvgSx = (theme: Theme, tone: Tone) => ({
  '& .track': { stroke: sys(theme).surfaceContainerHigh },
  '& .fill': { stroke: toneColor(theme, tone) },
});

export const donutScoreSx = (theme: Theme) => ({
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  fontSize: 22,
  fontWeight: 700,
  color: sys(theme).onBackground,
  fontVariantNumeric: 'tabular-nums',
});

export const factorMetaRowSx = { mb: '5px', fontSize: 12 } as const;

export const factorLabelSx = (theme: Theme) => ({
  fontSize: 12,
  color: sys(theme).onSurfaceVariant,
});

export const factorValueSx = (theme: Theme) => ({
  fontSize: 12,
  fontWeight: 700,
  color: sys(theme).onBackground,
  fontVariantNumeric: 'tabular-nums',
});

export const factorTrackSx = (theme: Theme) => ({
  height: 4,
  borderRadius: '999px',
  backgroundColor: sys(theme).surfaceContainerHigh,
  overflow: 'hidden',
});

export const factorFillSx = (theme: Theme, value: number) => ({
  height: 4,
  width: `${value}%`,
  borderRadius: '999px',
  backgroundColor: sys(theme).primary,
});

export const notEnoughCopySx = (theme: Theme, note?: string) => ({
  fontSize: 12,
  color: sys(theme).onSurfaceVariant,
  mb: note ? '13px' : 0,
});

export const notEnoughIconSx = (theme: Theme) => ({
  color: sys(theme).onSurfaceVariant,
  mt: '2px',
  flex: 'none',
  '& svg': { width: 17, height: 17 },
});

export const notEnoughNoteSx = (theme: Theme) => ({
  fontSize: 12.5,
  lineHeight: 1.5,
  color: sys(theme).onSurfaceVariant,
});

export const healthScoreSx = (theme: Theme, tone: Tone) => ({
  fontSize: 15,
  fontWeight: 700,
  color: toneColor(theme, tone),
  fontVariantNumeric: 'tabular-nums',
});

export const moodCopySx = (theme: Theme) => ({
  fontSize: 11.5,
  color: sys(theme).onSurfaceVariant,
});

export const driverRowSx = (theme: Theme) => ({
  display: 'grid',
  gridTemplateColumns: '12px 1fr',
  gap: '9px',
  alignItems: 'start',
  fontSize: 12.5,
  lineHeight: 1.45,
  color: sys(theme).onSurface,
});

export const driverSignSx = (theme: Theme, positive: boolean) => ({
  fontWeight: 800,
  color: positive ? sys(theme).success : signature(theme),
});

export const briefActionSx = (theme: Theme) => ({
  position: 'relative',
  paddingLeft: '15px',
  fontSize: 12.5,
  lineHeight: 1.55,
  color: sys(theme).onSurface,
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '1px',
    top: '8px',
    width: 4,
    height: 4,
    borderRadius: '999px',
    backgroundColor: sys(theme).primary,
  },
});

export const summaryBodySx = (theme: Theme) => ({
  fontSize: 13,
  lineHeight: 1.55,
  color: sys(theme).onSurface,
});

export const loadingCopySx = (theme: Theme) => ({
  fontSize: 12.5,
  color: sys(theme).onSurfaceVariant,
});
